// @flow

import { transparentize } from 'polished';
import React, { useCallback, type Node } from 'react';
import styled, { type StyledComponent } from 'styled-components';

import { fontFamily, colorPalette, sizes, media } from '../../../styles/GlobalStyle';

type ListItemProps = {
  id?: string,
  className?: string,
  children: Node,
  active?: boolean,
  hovered?: boolean,
  value?: string,
  onClick?: (event: string) => void,
  disabled?: boolean,
};

const RawListItem = ({
  id,
  className,
  children,
  onClick,
  value,
  active = false,
  hovered = false,
  disabled = false,
}: ListItemProps) => {
  const onClickLocal = useCallback(() => {
    if (onClick && value && !disabled) {
      onClick(value);
    }
  }, [disabled, onClick, value]);

  return (
    <div
      id={id}
      className={`${String(className)}${active ? ' active' : ''}${hovered ? ' hovered' : ''}${
        disabled ? ' disabled' : ''
      }`}
      role={'button'}
      tabIndex={0}
      onKeyPress={() => {}}
      onClick={onClickLocal}
    >
      {children}
    </div>
  );
};

export const ListItem: StyledComponent<ListItemProps, {}, typeof RawListItem> = styled(RawListItem)`
  display: flex;
  align-items: center;
  border: 2px solid ${colorPalette.secondary.hover};
  border-radius: ${sizes.borderRadius};
  padding: 20px;
  background: ${colorPalette.secondary.active};
  color: ${colorPalette.secondary.foreGround};
  text-align: left;
  text-transform: uppercase;
  ${({ theme }) =>
    theme.normalCase
    && `
    text-transform: initial;
  `}
  font-weight: ${fontFamily.primary.black.fontWeight};
  cursor: pointer;
  outline: 0;
  transition: 300ms all ease-out;
  user-select: none;

  ${({ theme }) =>
    theme.filtered
    && `
    color: ${transparentize(0.5, colorPalette.secondary.foreGround)};

    strong {
      color: ${colorPalette.secondary.foreGround};
    }
  `}

  &.active {
    background: ${colorPalette.primary.active};
    color: ${colorPalette.primary.foreGround};
    border-color: ${colorPalette.primary.hover};

    ${({ theme }) =>
      theme.filtered
      && `
      color: ${transparentize(0.5, colorPalette.primary.foreGround)};

      strong {
        color: ${colorPalette.primary.foreGround};
      }
    `}
  }

  &.disabled {
    cursor: initial;
  }

  ${media.greaterThan('desktop')`
    &:not(.active)&:not(.disabled) {
      &:hover, &.hovered {
        color: #fff;
        background-color: ${colorPalette.dark.active};

        ${({ theme }) =>
          theme.filtered
          && `
          color: ${transparentize(0.5, colorPalette.secondary.foreGround)};

          strong {
            color: ${colorPalette.secondary.foreGround};
          }
        `}
        
        &:after {
          opacity: 1;
          transform: scale(1, 1);
        }
      }
    }
  `}
`;
